<template>
  <v-col class="d-flex flex-column align-stretch">
    <view-top
      class="flex-grow-0 flex-shrink-0"
      :service="service"
      :settings="settings"
      :editableCols="editableCols"
      :colsChecksVisibility="colsChecksVisibility"
      @onQuickSearchChanged="onQuickSearchChanged($event)"
      @onTableRowsChange="onTableRowsChange($event)"
      @updateColsChecksVisibility="updateColsChecksVisibility($event)"
      @toggleColumn="toggleColumn($event)"
      @getCsv="getCsv()"
      @createTableItem="createTableItem"
      :dialogLargeSize="dialogLargeSize"
      :createTitle="createTitle"
      :modelKeyTitle="modelKeyTitle"
    />

    <v-row class="flex-grow-1 flex-shrink-0">
      <v-col style="position: relative" :class="showBulkActionsPanel === true ? 'pb-12' : ''">
        <ag-grid-vue
          style="width: 100%"
          :class="darkMode ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'"
          class="fill-height"
          rowSelection="multiple"
          rowDeselection="true"
          :columnDefs="columnDefs"
          :rowData="rowData"
          :gridOptions="gridOptions"
          :pagination="true"
          :defaultColDef="defaultColDef"
          :paginationPageSize="settings.tableRows"
          :columnTypes="columnTypes"
          :context="context"
          :frameworkComponents="frameworkComponents"
          @row-selected="onSelectionChanged"
          @column-moved="moveColumn"
          @column-resized="resizeColumn"
          @grid-ready="onGridReady"
          :overlayLoadingTemplate="loadingTemplate"
          :overlayNoRowsTemplate="noRowsTemplate"
        />
      </v-col>
      <bulk-actions-panel
        :showBulkActionsPanel="showBulkActionsPanel"
        :selectedRows="selectedRows"
        :settings="settings"
        @bulkDelete="bulkDelete"
        @getCsv="getCsv($event)"
      />
    </v-row>
  </v-col>
</template>

<script>
import "ag-grid-community/dist/styles/ag-grid.css"
import "ag-grid-community/dist/styles/ag-theme-alpine.css"
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css"
import { AgGridVue } from "ag-grid-vue"

import IndexActions from "@/components/crud_ag/IndexActions.vue"
import IndexMediaVideo from "@/components/crud_ag/IndexMediaVideo"
import IndexMediaImage from "@/components/crud_ag/IndexMediaImage"
import CustomFilter from "./filters/CustomFilter"

export default {
  components: {
    AgGridVue,
    ViewTop: () => import("@/components/crud_ag/commons/ViewTop.vue"),
    BulkActionsPanel: () => import("@/components/crud_ag/commons/ActionsPanel"),
  },
  props: {
    service: {
      type: Object,
      required: true,
    },
    customFunctions: {
      type: Object,
      required: false,
    },
    crudDialog: {
      type: Object,
      default: () => {
        return {
          large: false,
          createTitle: "Nou element",
          modelKeyTitle: "nom",
        }
      },
    },
    editCustomView: {
      type: Object,
      required: false,
    },
  },
  data: () => ({
    gridApi: null,
    gridColumnApi: null,
    rowData: null,
    columnDefs: null,
    context: null,
    settings: {
      columnsHidden: [],
      tableRows: "25",
    },
    defaultColDef: null,
    gridOptions: null,
    rowSelection: null,
    columnTypes: null,
    colsChecksVisibility: [],
    showBulkActionsPanel: null,
    selectedRows: null,
    frameworkComponents: null,
    loadingTemplate: null,
    noRowsTemplate: null,
  }),
  methods: {
    onGridReady() {
      this.getHeaders()
      this.getItems()
    },
    moveColumn() {
      const data = this.columnData()
      this.service.saveColumns(data).catch(error => {
        this.$notify({
          type: "error",
          title: this.$store.state.commonText.notification.errorTitle,
          text: this.$store.state.commonText.notification.errorText + "<br>" + error,
        })
      })
    },
    resizeColumn(params) {
      if (params.source === "uiColumnDragged" && params.finished) {
        params.api.resetRowHeights()
        const data = this.columnData()
        this.service.saveColumns(data).catch(error => {
          this.$notify({
            type: "error",
            title: this.$store.state.commonText.notification.errorTitle,
            text: this.$store.state.commonText.notification.errorText + "<br>" + error,
          })
        })
      }
    },
    processCell(params) {
      return params.value
    },
    onTableRowsChange(value) {
      this.settings.tableRows = value
      this.gridApi.paginationSetPageSize(Number(this.settings.tableRows))
    },
    onQuickSearchChanged(value) {
      this.gridApi.setQuickFilter(value)
    },
    updateColsChecksVisibility(value) {
      this.colsChecksVisibility = value
    },
    toggleColumn(column) {
      const column_id = column.field
      const visible = this.colsChecksVisibility.indexOf(column_id) !== -1
      this.showColumns([column_id], visible)
      const data = this.columnData()
      this.service.saveColumns(data).catch(error => {
        this.$notify({
          type: "error",
          title: this.$store.state.commonText.notification.errorTitle,
          text: this.$store.state.commonText.notification.errorText + "<br>" + error,
        })
      })
    },
    showColumns(columns, show) {
      this.gridColumnApi.setColumnsVisible(columns, show)
    },
    getHeaders() {
      this.service
        .getListHeaders()
        .then(response => {
          this.columnDefs = response.data.headers
          this.settings = Object.assign(this.settings, response.data.settings)
        })
        .catch(error => {
          this.notifyError(error)
        })
    },
    getItems() {
      this.service
        .getListItems()
        .then(response => {
          this.showColumns(this.settings.columnsHidden, false)
          this.rowData = response.data
        })
        .catch(error => {
          this.notifyError(error)
        })
    },
    deleteItem(rowNode) {
      this.$confirm(
        "<p>Confirmes que vols eliminar el següent ítem?</p><h2 style='text-align:center'>ID #" +
          rowNode.data.id +
          "</h2>"
      ).then(confirm => {
        if (confirm) {
          // delete from database
          this.service
            .deleteItems([rowNode.data.id])
            .then(() => {
              // delete from grid
              let rowArr = []
              rowArr.push(rowNode.data)
              this.gridApi.applyTransaction({ remove: rowArr })
              this.notifySuccess()
            })
            .catch(error => {
              this.notifyError(error)
            })
        }
      })
    },
    updateTableItem(rowNode) {
      this.service
        .getListItem(rowNode.data.id)
        .then(response => {
          Object.assign(rowNode.data, response.data[0])
          this.gridApi.applyTransaction({ update: rowNode.data[0] })
          this.notifySuccess()
        })
        .catch(error => {
          this.notifyError(error)
        })
    },
    createTableItem(id) {
      this.service.getListItem(id).then(response => {
        const newItem = response.data
        this.gridApi.applyTransaction({ add: newItem })
      })
    },
    bulkDelete() {
      const items = this.gridOptions.api.getSelectedRows()
      const ids = items.map(item => item.id)
      this.service.deleteItems(ids).then(response => {
        if (response.status === 200) {
          this.gridApi.applyTransaction({ remove: items })
          this.notifySuccess()
        }
      })
    },
    onSelectionChanged() {
      this.selectedRows = this.gridApi.getSelectedRows()
      this.selectedRows.length > 1
        ? (this.showBulkActionsPanel = true)
        : (this.showBulkActionsPanel = false)
    },
    notifySuccess() {
      this.$notify({
        type: "success",
        title: this.$store.state.commonText.notification.successTitle,
        text: this.$store.state.commonText.notification.successText,
      })
    },
    notifyError(error) {
      this.$notify({
        type: "error",
        title: this.$store.state.commonText.notification.errorTitle,
        text: this.$store.state.commonText.notification.errorText + "<br>" + error,
      })
    },
    isFirstColumn(params) {
      const displayedColumns = params.columnApi.getAllDisplayedColumns()
      return displayedColumns[0] === params.column
    },
    normalize(str) {
      const s1 = "ÃÀÁÄÂÈÉËÊÌÍÏÎÒÓÖÔÙÚÜÛãàáäâèéëêìíïîòóöôùúüûÑñÇç"
      const s2 = "AAAAAEEEEIIIIOOOOUUUUaaaaaeeeeiiiioooouuuunncc"
      for (let i = 0; i < s1.length; i++)
        str = str.replace(new RegExp(s1.charAt(i), "g"), s2.charAt(i))
      return str.replace(/[^-A-Za-z0-9]+/g, "-").toLowerCase()
    },
    getCsv(onlySelected) {
      //console.log("export CSV")
      const file_name = this.$route.meta.title
      const time = Date.now().toString()
      const num = time.substr(time.length - 4)
      const csv_columns = this.editableCols.map(col => col.field)

      const params = {
        columnSeparator: ";",
        columnKeys: csv_columns,
        fileName: this.normalize(file_name) + "_" + num + ".csv",
        onlySelected: onlySelected,
        processCellCallback: this.processCell,
        suppressQuotes: true,
      }

      this.gridApi.exportDataAsCsv(params)
      // const csv = this.gridApi.getDataAsCsv(params)
      // const json = this.csvToJson(csv)
      // console.log("JSON:", json)
    },
    csvToJson(csv) {
      let lines = csv.split("\r\n")
      let result = []
      let headers = lines[0].split(";")
      for (let i = 1; i < lines.length; i++) {
        let obj = {}
        let currentline = lines[i].split(";")
        for (let j = 0; j < headers.length; j++) {
          obj[headers[j]] = currentline[j]
        }
        result.push(obj)
      }
      return JSON.stringify(result)
    },
    columnData() {
      const columns = this.gridColumnApi.getAllGridColumns()
      return columns.map(column => {
        return {
          field: column.getColId(),
          width: column.getActualWidth(),
          visible: column.isVisible(),
        }
      })
    },
  },
  beforeMount() {
    this.gridOptions = {
      suppressCellSelection: true,
      suppressColumnMoveAnimation: true,
    }

    this.context = { componentParent: this }

    this.defaultColDef = {
      filter: true,
      minWidth: 100,
      autoHeight: true,
      resizable: true,
      sortable: true,
      headerCheckboxSelection: this.isFirstColumn,
      checkboxSelection: this.isFirstColumn,
      headerCheckboxSelectionFilteredOnly: true,
      cellClass: "cell-wrap-text",
    }

    this.loadingTemplate = "<span class='ag-overlay-loading-center'>Carregant dades</span>"
    this.noRowsTemplate = "No hi ha dades a mostrar"

    this.frameworkComponents = { customFilter: CustomFilter }

    this.columnTypes = {
      smallColumn: {
        minWidth: 100,
      },
      largeColumn: {
        minWidth: 200,
      },
      emailColumn: {
        cellRenderer: function (params) {
          return "<a href='mailto:" + params.value + "'>" + params.value + "</a>"
        },
        minWidth: 200,
      },
      wrapText: {
        cellClass: "cell-wrap-text",
      },
      flex: {
        flex: 1,
      },
      imageColumn: {
        cellRendererFramework: IndexMediaImage,
        maxWidth: 100,
      },
      thumbColumn: {
        cellRenderer: function (params) {
          if (params.value) {
            return "<img src='" + params.value + "'>"
          }
          return null
        },
        minWidth: 140,
      },
      videoColumn: {
        cellRendererFramework: IndexMediaVideo,
        maxWidth: 100,
      },
      multilineColumn: {
        cellRenderer: function (params) {
          let value = params.value
          if (value) {
            value = params.value.replace(/\|\|/g, "<br>")
          }
          return value
        },
      },
      actionsColumn: {
        cellRendererFramework: IndexActions,
        editable: false,
        sortable: false,
        resizable: false,
        filter: false,
        suppressMovable: true,
        pinned: "right",
        minWidth: 80,
        width: 100,
      },
      dateColumn: {
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            let dateParts = cellValue.split("/")
            let day = Number(dateParts[0])
            let month = Number(dateParts[1]) - 1
            let year = Number(dateParts[2])
            let cellDate = new Date(day, month, year)
            if (cellDate < filterLocalDateAtMidnight) {
              return -1
            } else if (cellDate > filterLocalDateAtMidnight) {
              return 1
            } else {
              return 0
            }
          },
        },
      },
    }
  },
  mounted() {
    this.gridApi = this.gridOptions.api
    this.gridColumnApi = this.gridOptions.columnApi
  },
  computed: {
    editableCols() {
      return this.columnDefs ? this.columnDefs.filter(column => column.field !== "actions") : []
    },
    visibleColumns() {
      let columnsHidden = this.settings.columnsHidden
      let columns = this.editableCols.filter(column => !columnsHidden.includes(column.field))
      return columns.map(column => column.field)
    },
    darkMode() {
      return this.$store.state.userSettings.darkMode
    },
    dialogLargeSize() {
      return this.crudDialog.large
    },
    createTitle() {
      return this.crudDialog.createTitle
    },
    modelKeyTitle() {
      return this.crudDialog.modelKeyTitle
    },
  },
  watch: {
    visibleColumns(newValue) {
      this.colsChecksVisibility = newValue
    },
  },
}
</script>

<style lang="scss" scoped>
.pr-selectedRowActions {
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
}
</style>

<style lang="scss">
.ag-menu {
  max-height: 60%;
}
.ag-cell-value {
  line-height: 160%;
  padding-top: 8px;
  padding-bottom: 8px;
}
.cell-wrap-text {
  white-space: normal;
}
.ag-selection-checkbox.ag-invisible {
  display: none !important;
}
</style>
